import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, Bus, Building2, Users, Gift, Coffee, TrendingUp, Star,
  CheckCircle, ArrowRight, Sparkles, Phone, QrCode, Percent,
  Award, Crown, DollarSign, Share2, MessageCircle, MapPin, Clock,
  Smartphone, CreditCard, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { api } from '@/utils/apiClient';

export default function PartnerProgramPage() {
  const [programInfo, setProgramInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgramInfo = async () => {
      try {
        const res = await api.get('/api/partners/program-info');
        setProgramInfo(res.data);
      } catch (err) {
        console.error('Failed to fetch program info:', err);
      }
      setLoading(false);
    };
    fetchProgramInfo();
  }, []);

  const partnerTypes = [
    { type: 'taxi', icon: Car, title: 'Taxi Drivers', description: 'Share your code with every tourist fare you pick up', color: 'from-yellow-500 to-amber-600' },
    { type: 'tour_bus', icon: Bus, title: 'Tour Bus Operators', description: 'Recommend to groups visiting the Nadi area', color: 'from-blue-500 to-indigo-600' },
    { type: 'hotel', icon: Building2, title: 'Hotel Concierge', description: 'Suggest to guests looking for great coffee', color: 'from-purple-500 to-pink-600' },
    { type: 'other', icon: Users, title: 'Tour Guides & More', description: 'Anyone who interacts with tourists', color: 'from-teal-500 to-cyan-600' }
  ];

  const steps = [
    { step: 1, icon: Phone, title: 'Sign Up Free', description: 'Register with your phone number in just 30 seconds' },
    { step: 2, icon: QrCode, title: 'Get Your Code', description: 'Receive your unique BULA code instantly via SMS' },
    { step: 3, icon: Users, title: 'Share & Earn', description: 'Give your code to tourists - they save 5%, you earn rewards' },
    { step: 4, icon: Coffee, title: 'Enjoy Free Coffee', description: 'Visit Bulaccino, give your phone number, redeem your rewards!' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="partner-program-page">
      <Helmet>
        <title>Partner Program - Earn Free Coffee | Bulaccino Cafe Nadi, Fiji</title>
        <meta name="description" content="Join Bulaccino Cafe's Partner Program. Taxi drivers, hotel concierge, and tour guides earn free coffee by referring tourists to the best cafe in Nadi, Fiji." />
        <link rel="canonical" href="https://bulaccinocafe.com/partner-program" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white py-20 md:py-32">
        <div className="absolute inset-0 bg-black/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Free to Join • Earn Real Rewards</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Bulaccino Cafe Partner Program
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
              Earn free coffee and exclusive rewards by sharing Fiji's best cafe with tourists
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/partner-signup">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 rounded-xl shadow-lg" data-testid="join-now-btn">
                  Join Now - It's Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/partner">
                <Button size="lg" variant="outline" className="border-white/50 text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl">
                  Partner Login
                </Button>
              </Link>
            </div>
            
            {/* Quick Stats */}
            {programInfo?.stats && (
              <div className="mt-12 flex justify-center gap-8 md:gap-16">
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-bold">{programInfo.stats.active_partners}+</p>
                  <p className="text-white/70 text-sm">Active Partners</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-bold">{programInfo.stats.coffees_earned}+</p>
                  <p className="text-white/70 text-sm">Coffees Earned</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">How It Works</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start earning rewards in just 4 simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-card rounded-2xl p-6 border border-border h-full hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-muted-foreground/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Your Rewards</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The more you share, the more you earn
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Coffee Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-3xl p-8 border border-amber-200 dark:border-amber-800"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-amber-500 text-white flex items-center justify-center">
                  <Coffee className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Free Coffee</h3>
                  <p className="text-amber-600 dark:text-amber-400">Every 100 FJD in referrals</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">Coffees stack up - earn unlimited</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">No expiration date</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">Redeem anytime with your phone number</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-white/50 dark:bg-black/20 rounded-xl">
                <p className="text-sm text-muted-foreground">
                  <strong>Example:</strong> 300 FJD in referrals = 3 free coffees waiting for you!
                </p>
              </div>
            </motion.div>

            {/* VIP Status Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-3xl p-8 border border-purple-200 dark:border-purple-800"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center">
                  <Star className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">VIP Status</h3>
                  <p className="text-purple-600 dark:text-purple-400">At 1,000 FJD in referrals</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-foreground font-semibold">10% off EVERY purchase for life</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">Exclusive VIP recognition</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">Just give your phone number at checkout</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-white/50 dark:bg-black/20 rounded-xl">
                <p className="text-sm text-muted-foreground">
                  <strong>That's only 200 tourists</strong> at 5 FJD average each to unlock VIP forever!
                </p>
              </div>
            </motion.div>
          </div>

          {/* Tourist Benefit */}
          <div className="mt-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-2xl p-6 border border-teal-200 dark:border-teal-800 flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-teal-500 text-white flex items-center justify-center flex-shrink-0">
                <Percent className="w-8 h-8" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-xl font-bold text-foreground mb-1">Tourists Save 5%</h3>
                <p className="text-muted-foreground">
                  When tourists use your code, they get 5% off their purchase. It's a win-win - they save money, and you earn rewards!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Tiers - Detailed Earnings */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Your Partner Journey</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              As tourists spend more using your code, you unlock bigger discounts for yourself
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {/* Bronze Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 border border-amber-300 dark:border-amber-700 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-600 to-amber-700 text-white flex items-center justify-center mb-4">
                <Award className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-foreground mb-1">Bronze Partner</h3>
              <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 mb-2">10% OFF</p>
              <p className="text-sm text-muted-foreground mb-4">At 1,000 FJD tourist spending</p>
              <div className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
                <strong>Example:</strong> 200 tourists × 5 FJD average = Bronze!
              </div>
            </motion.div>

            {/* Silver Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 border border-gray-300 dark:border-gray-600 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-gray-400/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-400 to-gray-500 text-white flex items-center justify-center mb-4">
                <Award className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-foreground mb-1">Silver Partner</h3>
              <p className="text-3xl font-bold text-gray-500 dark:text-gray-400 mb-2">12.5% OFF</p>
              <p className="text-sm text-muted-foreground mb-4">At 2,000 FJD tourist spending</p>
              <div className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
                <strong>Example:</strong> 400 tourists × 5 FJD average = Silver!
              </div>
            </motion.div>

            {/* Gold Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-6 border border-yellow-400 dark:border-yellow-600 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-400/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 text-white flex items-center justify-center mb-4">
                <Star className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-foreground mb-1">Gold Partner</h3>
              <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">15% OFF</p>
              <p className="text-sm text-muted-foreground mb-4">At 3,000 FJD tourist spending</p>
              <div className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
                <strong>Example:</strong> 600 tourists × 5 FJD average = Gold!
              </div>
            </motion.div>

            {/* Platinum Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl p-6 border-2 border-purple-400 dark:border-purple-500 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-400/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center mb-4">
                <Crown className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-foreground mb-1">Platinum Partner</h3>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">20% OFF</p>
              <p className="text-sm text-muted-foreground mb-4">At 5,000 FJD tourist spending</p>
              <div className="text-xs text-muted-foreground bg-white/50 dark:bg-black/20 rounded-lg p-3">
                <strong>Elite status!</strong> Maximum lifetime discount
              </div>
            </motion.div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              <strong>Remember:</strong> You also earn <span className="text-primary font-semibold">1 free coffee for every 100 FJD</span> in tourist spending, regardless of tier!
            </p>
          </div>
        </div>
      </section>

      {/* How Tourists Use Your Code - Detailed */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">How Tourists Redeem Their 5% Discount</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Share these simple instructions with the tourists you meet
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Self-Service Kiosk */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-6 border border-border"
              >
                <div className="w-14 h-14 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                  <Smartphone className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-bold text-lg text-foreground mb-3">Self-Service Kiosk</h3>
                <ol className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                    <span>Browse menu and add items to cart</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                    <span>At checkout, find "Partner Referral Code"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                    <span>Enter your code (e.g., BULA-XXXX)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                    <span>5% discount applied automatically!</span>
                  </li>
                </ol>
              </motion.div>

              {/* Counter Order */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-6 border border-border"
              >
                <div className="w-14 h-14 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <CreditCard className="w-7 h-7 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-bold text-lg text-foreground mb-3">Counter / Walk-Up</h3>
                <ol className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                    <span>Order at the counter as normal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                    <span>When paying, tell the cashier:</span>
                  </li>
                  <li className="bg-primary/5 rounded-lg p-3 ml-7">
                    <span className="font-medium text-primary">"I have a partner code: BULA-XXXX"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                    <span>Cashier applies 5% discount!</span>
                  </li>
                </ol>
              </motion.div>

              {/* Online Order */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl p-6 border border-border"
              >
                <div className="w-14 h-14 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-4">
                  <QrCode className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-bold text-lg text-foreground mb-3">Online Order</h3>
                <ol className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                    <span>Order via bulaccinocafe.com/order</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                    <span>At checkout, find "Partner Referral Code"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                    <span>Enter your code and see discount apply</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                    <span>Pick up order at the cafe!</span>
                  </li>
                </ol>
              </motion.div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-6 border border-primary/20">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <Share2 className="w-10 h-10 text-primary flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground mb-1">You'll Get a Shareable Voucher</h4>
                  <p className="text-muted-foreground text-sm">
                    When you sign up, you'll receive a beautiful digital voucher with your unique code, the cafe location, and all the instructions above. 
                    Share it via WhatsApp, text, or show it on your phone!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Can Join */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Perfect Partners</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              If you interact with tourists in Fiji, this program is for you
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {partnerTypes.map((partner, index) => (
              <motion.div
                key={partner.type}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card rounded-2xl overflow-hidden border border-border hover:shadow-xl transition-all group"
              >
                <div className={`h-2 bg-gradient-to-r ${partner.color}`}></div>
                <div className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${partner.color} text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <partner.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-lg text-foreground mb-2">{partner.title}</h3>
                  <p className="text-sm text-muted-foreground">{partner.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {programInfo?.faq && (
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Common Questions</h2>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4">
              {programInfo.faq.map((item) => (
                <motion.div
                  key={`faq-${item.question.slice(0, 30)}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-xl p-6 border border-border"
                >
                  <h3 className="font-semibold text-foreground mb-2">{item.question}</h3>
                  <p className="text-muted-foreground">{item.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Earning?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-xl mx-auto">
              Join the Bulaccino Cafe Partner Program today. It's free, easy, and rewarding!
            </p>
            <Link to="/partner-signup">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 rounded-xl shadow-lg">
                Sign Up Now - Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
